import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../store';
import { AccessType } from '../services/userApi';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredAccess?: AccessType | AccessType[];
}

/**
 * Composant de route protégée qui vérifie l'authentification et les droits d'accès
 * @param children Composant enfant à rendre si l'accès est autorisé
 * @param requiredAccess Accès requis pour accéder à la route (admin, inventory, rh, etc.)
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredAccess = []
}) => {
  const location = useLocation();
  const { isAuthenticated, currentUser } = useAppSelector(state => state.user);
  
  // Convertir en tableau si une seule chaîne est passée
  const requiredAccessArray = Array.isArray(requiredAccess) 
    ? requiredAccess 
    : [requiredAccess];
  
  // Si l'utilisateur n'est pas authentifié, rediriger vers la page de connexion
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si aucun accès spécifique n'est requis, l'utilisateur authentifié peut accéder
  if (requiredAccessArray.length === 0) {
    return <>{children}</>;
  }

  // Vérifier si l'utilisateur a les accès requis
  const userAccesses = currentUser?.accesses || [];
  
  // Si "admin" est dans les accès de l'utilisateur, il a accès à tout
  if (userAccesses.includes('admin' as AccessType)) {
    return <>{children}</>;
  }
  
  // Vérifier si l'utilisateur a au moins un des accès requis
  const hasRequiredAccess = requiredAccessArray.some(access => 
    userAccesses.includes(access)
  );

  if (!hasRequiredAccess) {
    // Rediriger vers la page d'accueil si l'accès est refusé
    return <Navigate to="/" replace />;
  }

  // Si tous les contrôles sont passés, afficher le composant enfant
  return <>{children}</>;
};

export default ProtectedRoute;
